import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import DateInputMDY from "@/components/DateInputMDY";
import { RefreshCw, Copy, ImagePlus, Trash2, UserPlus, Users, X, Mail, Theater, Ticket } from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const ROLE_OPTIONS = [
  { value: "org_admin",        label: "Org Admin" },
  { value: "producer",         label: "Producer" },
  { value: "production_staff", label: "Production Staff" },
  { value: "support_staff",    label: "Support Staff" },
  { value: "cast",             label: "Cast" },
];

/**
 * Comprehensive show configuration dialog for the Season Planner.
 *
 * One dialog, five sections — Details / Poster / Email / Auditions / Enroll /
 * Company — so an Org Admin never has to leave the Season Planner to set up
 * a new show or edit an existing one. In "new" mode Company + Poster + Enroll
 * are hidden until the show exists (they need an ID to hang off).
 */
export default function ShowEditor({ open, onClose, production, seasons, onSaved }) {
  const isNew = !production;
  const [form, setForm] = useState({
    title: "", tagline: "", venue: "", director: "",
    opening_night: "", closing_night: "",
    theater_name: "", season_id: null,
    email_from_name: "", email_from_address: "",
    audition_public: false, audition_pitch: "",
  });
  const [saving, setSaving] = useState(false);

  // Reset form on open/production change
  useEffect(() => {
    if (!open) return;
    setForm({
      title: production?.title || "",
      tagline: production?.tagline || "",
      venue: production?.venue || "",
      director: production?.director || "",
      opening_night: production?.opening_night || "",
      closing_night: production?.closing_night || "",
      theater_name: production?.theater_name || "",
      season_id: production?.season_id || null,
      email_from_name: production?.email_from_name || "",
      email_from_address: production?.email_from_address || "",
      audition_public: !!production?.audition_public,
      audition_pitch: production?.audition_pitch || "",
    });
  }, [open, production]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        tagline: form.tagline,
        venue: form.venue,
        director: form.director,
        opening_night: form.opening_night || null,
        closing_night: form.closing_night || null,
        theater_name: form.theater_name,
        season_id: form.season_id || null,
        email_from_name: form.email_from_name,
        email_from_address: form.email_from_address || null,
        audition_public: form.audition_public,
        audition_pitch: form.audition_pitch,
      };
      let saved;
      if (isNew) {
        // Backend only accepts a subset on POST; the rest goes via PATCH.
        const create = await api.post("/productions", {
          title: body.title, tagline: body.tagline, venue: body.venue,
          director: body.director, opening_night: body.opening_night || "",
          closing_night: body.closing_night || "",
          season_id: body.season_id,
        });
        saved = create.data;
        // Patch the rest that the create model doesn't accept
        const remainder = {
          theater_name: body.theater_name,
          email_from_name: body.email_from_name,
          email_from_address: body.email_from_address,
          audition_public: body.audition_public,
          audition_pitch: body.audition_pitch,
        };
        if (Object.values(remainder).some((v) => v !== "" && v !== null && v !== false)) {
          await api.patch(`/productions/${saved.id}`, remainder);
        }
      } else {
        const r = await api.patch(`/productions/${production.id}`, body);
        saved = r.data;
      }
      toast.success(isNew ? "Show created" : "Saved");
      onSaved?.(saved);
      if (isNew) onClose();  // For edit we stay open so admin can keep tweaking
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="show-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isNew ? "Create a new show" : `Manage ${production?.title || "show"}`}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Everything you&apos;d normally set in Settings — details, poster, email sender, auditions, enroll code, and company — in one place.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <ShowDetailsSection form={form} setForm={setForm} seasons={seasons} />
          {!isNew && (
            <>
              <PosterSection production={production} onSaved={onSaved} />
              <EnrollCodeSection production={production} onSaved={onSaved} />
            </>
          )}
          <EmailSection form={form} setForm={setForm} />
          <AuditionSection form={form} setForm={setForm} />
          {!isNew && <CompanySection production={production} />}
        </div>

        <DialogFooter className="mt-4">
          <button onClick={onClose} className="h-9 px-4 rounded-md border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5 text-sm">Close</button>
          <button data-testid="save-show-btn" onClick={save} disabled={saving} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 disabled:opacity-60">
            {saving ? "Saving…" : (isNew ? "Create show" : "Save changes")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
      <header className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-gold" />}
        <div>
          <div className="font-display text-lg">{title}</div>
          {hint && <div className="text-[11px] text-zinc-500">{hint}</div>}
        </div>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function ShowDetailsSection({ form, setForm, seasons }) {
  return (
    <Section icon={Theater} title="Show details" hint="What the show is called and when.">
      <div><Label>Title *</Label><Input data-testid="show-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
      <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Venue</Label><Input value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
        <div><Label>Director</Label><Input value={form.director} onChange={(e) => setForm({ ...form, director: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Opening night</Label><DateInputMDY value={form.opening_night} onChange={(v) => setForm({ ...form, opening_night: v || "" })} /></div>
        <div><Label>Closing night</Label><DateInputMDY value={form.closing_night} onChange={(v) => setForm({ ...form, closing_night: v || "" })} /></div>
      </div>
      <div><Label>Theatre / organisation name</Label><Input value={form.theater_name} onChange={(e) => setForm({ ...form, theater_name: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="Shown on printed call sheets and audition pages" /></div>
      <div>
        <Label>Season</Label>
        <Select value={form.season_id || "__none"} onValueChange={(v) => setForm({ ...form, season_id: v === "__none" ? null : v })}>
          <SelectTrigger data-testid="show-season-select" className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="Unassigned" /></SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 text-white">
            <SelectItem value="__none">Unassigned</SelectItem>
            {seasons.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </Section>
  );
}

function PosterSection({ production, onSaved }) {
  const fileRef = useRef(null);
  const thumbUrl = production?.thumbnail_url ? `${API_BASE}${production.thumbnail_url}` : null;
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    await api.post(`/productions/${production.id}/thumbnail`, fd);
    toast.success("Poster updated");
    onSaved?.();
  };
  return (
    <Section icon={ImagePlus} title="Poster" hint="Show artwork — appears on every card.">
      <div className="flex items-start gap-4">
        <div className="w-32 h-40 rounded-md border border-white/10 bg-[#0A0A0A] overflow-hidden shrink-0 grid place-items-center">
          {thumbUrl ? <img src={thumbUrl} alt="" className="w-full h-full object-cover" /> : <Theater className="w-6 h-6 text-zinc-600" />}
        </div>
        <div className="flex-1">
          <button data-testid="upload-poster-btn" onClick={() => fileRef.current?.click()} className="h-9 px-3 rounded-md border border-white/15 hover:border-gold hover:text-gold text-sm inline-flex items-center gap-1.5">
            <ImagePlus className="w-4 h-4" /> Upload poster
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; upload(f); e.target.value = ""; }} />
          <div className="text-[11px] text-zinc-500 mt-2">JPG/PNG, portrait or landscape. Recommended 800×1000+.</div>
        </div>
      </div>
    </Section>
  );
}

function EnrollCodeSection({ production, onSaved }) {
  const regen = async () => {
    if (!confirm("Regenerate enrollment code? The old code will stop working immediately.")) return;
    await api.post(`/productions/${production.id}/regenerate-code`);
    toast.success("Code regenerated");
    onSaved?.();
  };
  const copy = () => {
    try { navigator.clipboard.writeText(production.enroll_code); } catch (_) { /* clipboard unavailable */ }
    toast.success("Code copied");
  };
  return (
    <Section icon={Ticket} title="Enrollment code" hint="Share this code — anyone with it can self-enroll as Cast.">
      <div className="flex items-center gap-2 rounded-md border border-white/10 bg-[#0A0A0A] p-3">
        <code data-testid="show-enroll-code" className="font-mono text-gold text-lg tracking-[0.35em] flex-1">{production.enroll_code}</code>
        <button onClick={copy} className="p-2 rounded border border-white/10 hover:border-gold hover:text-gold"><Copy className="w-4 h-4" /></button>
        <button onClick={regen} data-testid="regen-code-btn" className="p-2 rounded border border-white/10 hover:border-gold hover:text-gold"><RefreshCw className="w-4 h-4" /></button>
      </div>
    </Section>
  );
}

function EmailSection({ form, setForm }) {
  return (
    <Section icon={Mail} title="Email sender" hint="Name and address used when the show emails cast, staff, or audition applicants.">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>From name</Label><Input value={form.email_from_name} onChange={(e) => setForm({ ...form, email_from_name: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="Willoughby Theatre" /></div>
        <div><Label>From address</Label><Input type="email" value={form.email_from_address} onChange={(e) => setForm({ ...form, email_from_address: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="hello@yourtheatre.com" /></div>
      </div>
    </Section>
  );
}

function AuditionSection({ form, setForm }) {
  return (
    <Section title="Auditions" hint="Public discovery on /discover for this show.">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="audition-public-toggle" checked={form.audition_public} onChange={(e) => setForm({ ...form, audition_public: e.target.checked })} className="accent-gold" />
        List this show on the public auditions directory
      </label>
      <div><Label>Audition pitch (public)</Label><Textarea rows={3} value={form.audition_pitch} onChange={(e) => setForm({ ...form, audition_pitch: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="Short description shown on the public audition page." /></div>
    </Section>
  );
}

function CompanySection({ production }) {
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [q, setQ] = useState("");
  const [addingId, setAddingId] = useState(null);

  const load = async () => {
    const [m, u] = await Promise.all([
      api.get(`/productions/${production.id}/members`),
      api.get(`/users`).catch(() => ({ data: [] })), // super_admin gets everyone; admin gets [] here — we fall back to member-only view
    ]);
    setMembers(m.data);
    setAllUsers(u.data || []);
  };
  useEffect(() => { if (production?.id) load(); }, [production?.id]);

  const promote = async (userId, newRole) => {
    try {
      await api.patch(`/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const add = async (userId) => {
    setAddingId(userId);
    try {
      await api.post(`/productions/${production.id}/members`, { user_id: userId });
      toast.success("Added to show");
      load();
      setQ("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAddingId(null); }
  };

  const remove = async (userId) => {
    if (!confirm("Remove this person from the show?")) return;
    await api.delete(`/productions/${production.id}/members/${userId}`);
    load();
  };

  const memberIds = new Set(members.map((m) => m.id));
  const addable = allUsers.filter((u) => !memberIds.has(u.id) && (
    !q.trim() || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase())
  )).slice(0, 8);

  return (
    <Section icon={Users} title="Company" hint="Everyone on this show. Promote a Cast member to Producer / Production Staff / Support Staff here.">
      {members.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">No members yet.</div>
      ) : (
        <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {members.map((m) => (
            <li key={m.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-[#0A0A0A] px-2 py-2" data-testid={`member-row-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{m.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{m.email}</div>
              </div>
              <Select value={m.role} onValueChange={(v) => promote(m.id, v)}>
                <SelectTrigger data-testid={`member-role-${m.id}`} className="w-40 h-8 bg-[#121212] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <button onClick={() => remove(m.id)} className="p-1.5 rounded border border-white/10 text-zinc-400 hover:text-red-400 hover:border-red-500/40" title="Remove from show"><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}

      {allUsers.length > 0 && (
        <div className="pt-3 border-t border-white/5">
          <Label>Add existing person</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="bg-[#0A0A0A] border-white/10" placeholder="Search by name or email…" data-testid="member-search-input" />
          {q && (
            <ul className="mt-2 space-y-1">
              {addable.map((u) => (
                <li key={u.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-[#121212] px-2 py-1.5 text-sm" data-testid={`addable-user-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-white">{u.name} <span className="text-zinc-500 text-[11px]">({u.role})</span></div>
                    <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                  </div>
                  <button onClick={() => add(u.id)} disabled={addingId === u.id} className="h-7 px-2 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs inline-flex items-center gap-1"><UserPlus className="w-3 h-3" /> Add</button>
                </li>
              ))}
              {addable.length === 0 && <li className="text-[11px] text-zinc-500 italic">No matches. Ask them to self-enroll with the code first.</li>}
            </ul>
          )}
        </div>
      )}
      {allUsers.length === 0 && (
        <div className="text-[11px] text-zinc-500 italic pt-2 border-t border-white/5">
          Adding new people works via the enroll code above (Cast) then promoting them here. To search across the whole org, sign in as super_admin.
        </div>
      )}
    </Section>
  );
}
